/* eslint-disable no-unused-vars, array-bracket-newline, react-hooks/exhaustive-deps */
import { useCallback } from 'react';
import DesktopTable from '../../../molecules/Table/DesktopTable';
import TickerCell from '../TickerCell';
import Text from '../../../atoms/Text';
import { useTableData } from './useTableData';
import { formatTimestamp, formatTx } from '../formatters';
import { onTxScan, onWalletScan } from '../../../helpers/addressUtils';
import NumberDisplay from '../../../molecules/NumberDisplay';
import { ETableTimeframe } from '../helpers';
import TokenInfo from '../../../molecules/TokenInfo';
import { formatAddress } from '../../../helpers/formatters';
import {
  decorateExchangeName,
  decorateSwapType,
} from '../../../helpers/decorators';
import { IMyPosition, ISwap } from './useGetMyPositions';
import { roundToPrecision } from '../../../helpers/truncate';

export const LS_MY_TRADES_HIDDEN_COLUMNS_KEY =
  'kattana-simple.my_trades_table_columns_hidden';
export const LS_MY_TRADES_KEY = 'kattana-simple.my_trades_table_columns';

interface IMyPositionsTableProps {
  swaps: IMyPosition[];
  timeframe: ETableTimeframe;
  loading: boolean;
  loggedIn: boolean;
  timezone?: string;
  width?: number;
  handleSetTimeframe(day: ETableTimeframe): void;
  onTradeClick(swap: IMyPosition): void;
}

const buyClassName = 'row--Buy text--green-600';
const sellClassName = 'row--Sell text--red-600';

/**
 * Render a table
 * @param props
 * @returns
 */
const MyPositionsTable = ({
  swaps,
  timeframe,
  loading,
  loggedIn,
  timezone,
  width,
  handleSetTimeframe,
  onTradeClick,
}: IMyPositionsTableProps) => {
  const placeholder = loggedIn
    ? 'No data found'
    : 'Connect wallet to get access to my positions';

  const { columns, data, tableColumnsWidth, subColumns } = useTableData({
    swaps,
  });

  const headers = ['Date;Pair;Action;Price ($);Price;Value;Tx;Wallet']; // Headers
  const csvData = [
    ...headers,
    ...swaps.map(
      ({ timestamp, ticker, isBuy, priceUSD, price, valueUSD, tx, wallet }) =>
        `${formatTimestamp(timestamp, timezone)};${ticker?.name || '-'};${
          isBuy ? 'Buy' : 'Sell'
        };${roundToPrecision(priceUSD, true)};${roundToPrecision(
          price,
          true
        )};${roundToPrecision(valueUSD, true)};${tx};${wallet}`
    ),
  ];

  const renderCell = (cell: any) => {
    const {
      chain,
      srcTokenAddress,
      srcTokenName,
      srcTokenIcon,
      destTokenAddress,
      destTokenName,
      destTokenIcon,
    } = cell.row.original as IMyPosition;

    switch (cell.column.id) {
    case 'src':
      return (
        <TokenInfo
          chain={chain}
          address={srcTokenAddress}
          name={srcTokenName}
          amount={cell.value}
          icon={srcTokenIcon}
        />
      );
    case 'dest':
      return (
        <TokenInfo
          chain={chain}
          address={destTokenAddress}
          name={destTokenName}
          amount={cell.value}
          icon={destTokenIcon}
        />
      );
    case 'pair':
      return cell.value ? (
        <TickerCell
          path={`${chain}-${cell.value.address}`}
          ticker={cell.value}
        />
      ) : (
        <Text size={12}>-</Text>
      );
    case 'date':
      return <Text size={12}>{formatTimestamp(cell.value, timezone)}</Text>;
    case 'price':
      return (
        <NumberDisplay
          value={cell.value}
          endSymbol={` ${destTokenName}`}
          textProps={{ size: 12 }}
        />
      );
    case 'priceUSD':
      return (
        <NumberDisplay
          value={cell.value}
          endSymbol=' $'
          textProps={{ size: 12 }}
        />
      );
    case 'valueUSD':
      return (
        <NumberDisplay value={Number(cell.value)} textProps={{ size: 12 }} />
      );
    case 'wallet':
      return (
        <div
          className='tx'
          onClick={(e) => onWalletScan(cell.value, chain, e)}
        >
          <Text size={12}>{formatAddress(cell.value)}</Text>
        </div>
      );
    case 'tx':
      return (
        <div className='tx' onClick={(e) => onTxScan(cell.value, chain, e)}>
          <Text size={12}>{formatTx(cell.value)}</Text>
        </div>
      );
    case 'exchange':
      return <Text size={12}>{decorateExchangeName(cell.value, chain)}</Text>;
    case 'type':
      return <Text size={12}>{decorateSwapType(cell.value)}</Text>;
    default:
      return <Text size={12}>{cell.value}</Text>;
    }
  };

  /**
   * acknowledge buy/sell row color
   */
  const getClassNameForRow = useCallback(
    ({ isBuy }: IMyPosition) => (isBuy ? buyClassName : sellClassName),
    []
  );

  const renderTableControls = useCallback(
    () => (
      <>
        <div
          onClick={() => handleSetTimeframe(ETableTimeframe.DAY)}
          className={`pill ${
            timeframe == ETableTimeframe.DAY ? 'pill--active' : ''
          }`}
        >
          1d
        </div>
        <div
          onClick={() => handleSetTimeframe(ETableTimeframe.WEEK)}
          className={`pill ${
            timeframe == ETableTimeframe.WEEK ? 'pill--active' : ''
          }`}
          data-tip='Available only for premium subscription'
          data-for='top-wallets-timeframes-tooltip'
        >
          7d
        </div>
        <div
          onClick={() => handleSetTimeframe(ETableTimeframe.MONTH)}
          className={`pill ${
            timeframe == ETableTimeframe.MONTH ? 'pill--active' : ''
          }`}
        >
          1M
        </div>
      </>
    ),
    [timeframe]
  );

  const renderSubCell = (
    accessor: string,
    parentRow: IMyPosition,
    row: ISwap,
    value: any
  ) => {
    const {
      chain,
      srcTokenAddress,
      srcTokenName,
      srcTokenIcon,
      destTokenAddress,
      destTokenName,
      destTokenIcon,
    } = row;

    switch (accessor) {
    case 'srcTokenAmount':
      return (
        <TokenInfo
          chain={chain}
          address={srcTokenAddress}
          name={srcTokenName}
          amount={value}
          icon={srcTokenIcon}
          imageSize='m'
        />
      );
    case 'destTokenAmount':
      return (
        <TokenInfo
          chain={chain}
          address={destTokenAddress}
          name={destTokenName}
          amount={value}
          icon={destTokenIcon}
          imageSize='m'
        />
      );
    case 'ticker':
      return value ? (
        <TickerCell path={`${chain}-${value.address}`} ticker={value} />
      ) : (
        <Text size={12}>-</Text>
      );
    case 'price':
      return (
        <NumberDisplay
          value={value}
          endSymbol={` ${destTokenName}`}
          textProps={{ size: 12 }}
        />
      );
    case 'priceUSD':
      return (
        <NumberDisplay
          value={value}
          endSymbol=' $'
          textProps={{ size: 12 }}
        />
      );
    case 'valueUSD':
      return <NumberDisplay value={Number(value)} textProps={{ size: 12 }} />;
    case 'exchange':
      return <Text size={12}>{decorateExchangeName(value, chain)}</Text>;
    case 'swapType':
      return <Text size={12}>{decorateSwapType(value)}</Text>;
    case 'isBuy':
      return (
        <Text
          size={12}
          className={`text--${value ? 'green-600' : 'red-600'}`}
        >
          {value ? 'Buy' : 'Sell'}
        </Text>
      );
    default:
      return <Text size={12}>{value}</Text>;
    }
  };

  return (
    <DesktopTable
      hiddenColumnsKey={LS_MY_TRADES_HIDDEN_COLUMNS_KEY}
      dataKey={LS_MY_TRADES_KEY}
      title='my_trades'
      tableColumnsWidth={tableColumnsWidth}
      tableWidth={width || 0}
      columns={columns}
      subColumns={subColumns}
      data={data}
      hiddenColumns={['blocknumber', 'wallet', 'order']}
      defaultSortedColumn={{ id: 'date', desc: true }}
      extendedRowId='subTrades'
      placeholderText={placeholder}
      csvData={csvData}
      loading={loading}
      getClassNameForRow={getClassNameForRow}
      renderCell={renderCell}
      renderSubCell={renderSubCell}
      renderTableControls={renderTableControls}
      onRowClick={onTradeClick}
      onSubRowClick={onTradeClick}
    />
  );
};

export default MyPositionsTable;
